<template>
  <v-col>
    <v-card v-if="this.reportType.length > 0 && this.insurance.length > 0 && this.reportData.length > 0">
      <v-card-title primary-title>
        <v-row>
          <v-col lg="4" sm="12">{{ this.reportType }}</v-col>
        </v-row>
      </v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="6" class="pt-0" text-right>
            <v-text-field
              class="py-0"
              v-model="search"
              label="Search"
              append-icon="mdi-search"
              single-line
              hide-details
            ></v-text-field>
          </v-col>
          <v-col cols="6" class="pt-0">
            <v-select
              class="pt-0"
              v-model="selectedHeaders"
              :items="headerOptions"
              :menu-props="{ maxHeight: '400' }"
              label="Fields"
              multiple
              return-object
            >
              <v-list-item slot="prepend-item" @click="toggle">
                <v-list-item-title>Select All</v-list-item-title>
              </v-list-item>
              <template v-slot:selection="{ item, index }">
                <v-chip small v-if="index < 2">
                  <span>{{ item.text }}</span>
                </v-chip>
                <span v-if="index === 2" class="grey--text caption"
                  >(+{{ selectedHeaders.length - 2 }} others)</span
                >
              </template>
            </v-select>
          </v-col>
        </v-row>
      </v-card-text>
      <v-data-table
        :loading="load"
        :headers="selectedHeaders"
        :items="filteredData"
        :search="search"
        :footer-props="{
          'items-per-page-options': [25, 50, 75, 100, -1]
        }"
        :items-per-page="25"
        multi-sort
        show-group-by
        loading-text="Loading data..."
        class="elevation-0 col-md-12 m-auto"
      >
        <template v-slot:item.UNSCALED_RISK_SCORE="{ item }">
          {{ item.UNSCALED_RISK_SCORE.toFixed(2) }}
        </template>

        <template v-slot:item.RESCALED_CONCURRENT_RISK="{ item }">
          {{ item.RESCALED_CONCURRENT_RISK.toFixed(2) }}
        </template>

        <template v-slot:item.INPATIENT_HOSPITALIZATION_COUNT="{ item }">
          <v-chip dark :color="getChipColor(item.INPATIENT_HOSPITALIZATION_COUNT)">
            {{ item.INPATIENT_HOSPITALIZATION_COUNT }}
          </v-chip>
        </template>

        <template v-slot:item.INPATIENT_HOSPITALIZATION_DAYS="{ item }">
          <v-chip dark :color="getChipColor(item.INPATIENT_HOSPITALIZATION_DAYS)">
            {{ item.INPATIENT_HOSPITALIZATION_DAYS }}
          </v-chip>
        </template>

        <template v-slot:item.EMERGENCY_VISIT_COUNT="{ item }">
          <v-chip dark :color="getChipColor(item.EMERGENCY_VISIT_COUNT)">
            {{ item.EMERGENCY_VISIT_COUNT }}
          </v-chip>
        </template>

        <template v-slot:item.OUTPATIENT_VISIT_COUNT="{ item }">
          <v-chip dark :color="getChipColor(item.OUTPATIENT_VISIT_COUNT)">
            {{ item.OUTPATIENT_VISIT_COUNT }}
          </v-chip>
        </template>

        <template v-slot:item.MAJOR_PROCEDURE="{ item }">
          <v-chip dark :color="getChipColor(item.MAJOR_PROCEDURE)">
            {{ item.MAJOR_PROCEDURE }}
          </v-chip>
        </template>

        <template v-slot:item.CANCER_TREATMENT="{ item }">
          <v-chip dark :color="getChipColor(item.CANCER_TREATMENT)">
            {{ item.CANCER_TREATMENT }}
          </v-chip>
        </template>

        <template v-slot:item.PSYCHOTHERAPY_SERVICE="{ item }">
          <v-chip dark :color="getChipColor(item.PSYCHOTHERAPY_SERVICE)">
            {{ item.PSYCHOTHERAPY_SERVICE }}
          </v-chip>
        </template>

        <template v-slot:item.UNPLANNED_READMISSION_30_DAY_COUNT="{ item }">
          <v-chip dark :color="getChipColor(item.UNPLANNED_READMISSION_30_DAY_COUNT)">
            {{ item.UNPLANNED_READMISSION_30_DAY_COUNT }}
          </v-chip>
        </template>

        <template v-slot:item.ALL_CAUSE_INPATIENT_COUNT="{ item }">
          <v-chip dark :color="getChipColor(item.ALL_CAUSE_INPATIENT_COUNT)">
            {{ item.ALL_CAUSE_INPATIENT_COUNT }}
          </v-chip>
        </template>

        <template v-slot:item.UNPLANNED_INPATIENT_HOSPITALIZATION_COUNT="{ item }">
          <v-chip dark :color="getChipColor(item.UNPLANNED_INPATIENT_HOSPITALIZATION_COUNT)">
            {{ item.UNPLANNED_INPATIENT_HOSPITALIZATION_COUNT }}
          </v-chip>
        </template>

        <template v-slot:item.READMISSION_30_DAY_COUNT="{ item }">
          <v-chip dark :color="getChipColor(item.READMISSION_30_DAY_COUNT)">
            {{ item.READMISSION_30_DAY_COUNT }}
          </v-chip>
        </template>

        <template v-slot:item.RESOURCE_UTILIZATION_BAND="{ item }">
          <v-chip dark :color="getChipColor(item.RESOURCE_UTILIZATION_BAND)">
            {{ item.RESOURCE_UTILIZATION_BAND }}
          </v-chip>
        </template>

        <template v-slot:item.HOSPITAL_DOMINANT_MORBIDITY_TYPES="{ item }">
          <v-chip dark :color="getChipColor(item.HOSPITAL_DOMINANT_MORBIDITY_TYPES)">
            {{ item.HOSPITAL_DOMINANT_MORBIDITY_TYPES }}
          </v-chip>
        </template>

        <template v-slot:item.CHRONIC_CONDITION_COUNT="{ item }">
          <v-chip dark :color="getChipColor(item.CHRONIC_CONDITION_COUNT)">
            {{ item.CHRONIC_CONDITION_COUNT }}
          </v-chip>
        </template>

        <template v-slot:item.DIAGNOSES_USED="{ item }">
          <v-chip dark :color="getChipColor(item.DIAGNOSES_USED)">
            {{ item.DIAGNOSES_USED }}
          </v-chip>
        </template>

       <template v-slot:item.ID="{ item }">
          <v-chip
            dark
            @click="openReferenceVsLocalRisk(item.ID, item.member_name, item.pcp_name, item.PRODUCT)"
            >{{ item.ID }}</v-chip
          >
        </template>

        <template v-slot:group.header="{ group, headers, isOpen, toggle, remove }">
          <td style="background: #fff;" :colspan="headers.length">
            <v-btn @click="toggle" small icon :ref="group" :data-open="isOpen">
              <v-icon v-if="isOpen">mdi-chevron-up</v-icon>
              <v-icon v-else>mdi-chevron-down</v-icon>
            </v-btn>
            <span class="font-weight-bold">{{ group }}</span>
            <v-icon small @click="remove">mdi-close</v-icon>
          </td>
        </template>


        <template v-slot:item.DETAILS="{ item }">
          <patient-modal :patient="item" :insurance="insurance" />
        </template>

        <template slot="footer">
          <ExportCSV
            :data="this.csvData"
            :name="
              'Top ' +
                this.reportLimit * 100 +
                '% ' +
                this.reportType +
                ' ' +
                this.startDate +
                '.csv'
            "
          >
            <v-btn color="secondary" text small class="export-button">Export to CSV</v-btn>
          </ExportCSV>
        </template>
      </v-data-table>
         <AiSubReportModal
        :obsPeriod="observationPeriod"
        :memName="memberName"
        :provName="provName"
        :insurance="insurance"
        :startDate="startDate"
        :endDate="endDate"
        :groups="groups"
        :providers="providers"
        :locations="locations"
        :patientID="AiSubReportModalID"
        v-model="AiSubReportModalOverlay"
      />
    </v-card>
  </v-col>

</template>

<script>
import axios from 'axios';
import ExportCSV from 'vue-json-csv';
import PatientModal from '../PatientModal';
import AiSubReportModal from './AiSubReportModal';

export default {
  name: 'AiReports',
  props: [
    'organizations',
    'groups',
    'providers',
    'locations',
    'insurance',
    'startDate',
    'endDate',
    'reportType',
    'data',
    'csvData',
    'reportLimit'
  ],
  components: {
    PatientModal,
    AiSubReportModal,
    ExportCSV,
  },
  data() {
    return {
      riskToggle: '',
      search: '',
      load: false,
      reportData: [],
      filteredData: [],
      headerOptions: [],
      selectedHeaders: [],
      headers: [],
      value: [],
      AiSubReportModalOverlay: false,
      AiSubReportModalID: '',
      memberName: '',
      provName: '',
      observationPeriod: '',
    };
  },
  methods: {
    async getHeaders() {
      const reportType = this.reportType.toLowerCase();
      const insurance = this.insurance.toLowerCase();

      if (reportType === 'risk report') {
        this.selectedHeaders = [
          { text: 'Provider Name', value: 'pcp_name', width: 200 },
          { text: 'Patient ID', value: 'ID' },
          { text: 'Patient Name', value: 'member_name' },
          { text: 'DOB', value: 'DOB' },
          { text: 'Age', value: 'AGE' },
          { text: 'Sex', value: 'SEX' },
          { text: 'Reference Risk', value: 'UNSCALED_RISK_SCORE' },
          { text: 'Local Risk', value: 'RESCALED_CONCURRENT_RISK' },
          {
            text: 'INP Hosp Count',
            value: 'INPATIENT_HOSPITALIZATION_COUNT',
          },
          {
            text: 'INP Hosp Days',
            value: 'INPATIENT_HOSPITALIZATION_DAYS',
          },
          { text: 'ER Visit Count', value: 'EMERGENCY_VISIT_COUNT' },
          { text: 'Outpatient Visit Count', value: 'OUTPATIENT_VISIT_COUNT' },
          { text: 'Major Procedure', value: 'MAJOR_PROCEDURE' },
          { text: 'Cancer Treatment', value: 'CANCER_TREATMENT' },
          { text: 'PSY Therapy Service', value: 'PSYCHOTHERAPY_SERVICE' },
          {
            text: ' All Cause INP Hosp Count',
            value: 'ALL_CAUSE_INPATIENT_COUNT',
          },
          {
            text: 'Unplanned INP Hosp Count',
            value: 'UNPLANNED_INPATIENT_HOSPITALIZATION_COUNT',
          },
          {
            text: 'Unplanned 30 Day Readmit Count',
            value: 'UNPLANNED_READMISSION_30_DAY_COUNT',
          },
          {
            text: '30 Day Readmit Count',
            value: 'READMISSION_30_DAY_COUNT',
          },
          { text: ' Age Band', value: 'AGE_BAND' },
          {
            text: 'Resource Util Band',
            value: 'RESOURCE_UTILIZATION_BAND',
          },

          {
            text: 'Hosp Major Morbidity Count',
            value: 'HOSPITAL_DOMINANT_MORBIDITY_TYPES',
          },
          {
            text: 'Chronic Count',
            value: 'CHRONIC_CONDITION_COUNT',
          },
          { text: 'Diagnostic Count', value: 'DIAGNOSES_USED' },
          { text: 'Details', value: 'DETAILS' },
        ];

        this.headerOptions = [
          {
            text: 'INP Hosp Count',
            value: 'INPATIENT_HOSPITALIZATION_COUNT',
          },
          {
            text: 'INP Hosp Days',
            value: 'INPATIENT_HOSPITALIZATION_DAYS',
          },
          { text: 'ER Visit Count', value: 'EMERGENCY_VISIT_COUNT' },
          { text: 'Outpatient Visit Count', value: 'OUTPATIENT_VISIT_COUNT' },
          { text: 'Major Procedure', value: 'MAJOR_PROCEDURE' },
          { text: 'Cancer Treatment', value: 'CANCER_TREATMENT' },
          { text: 'PSY Therapy Service', value: 'PSYCHOTHERAPY_SERVICE' },
          {
            text: ' All Cause INP Hosp Count',
            value: 'ALL_CAUSE_INPATIENT_COUNT',
          },
          {
            text: 'Unplanned INP Hosp Count',
            value: 'UNPLANNED_INPATIENT_HOSPITALIZATION_COUNT',
          },
          {
            text: 'Unplanned 30 Day Readmit Count',
            value: 'UNPLANNED_READMISSION_30_DAY_COUNT',
          },
          {
            text: '30 Day Readmit Count',
            value: 'READMISSION_30_DAY_COUNT',
          },
          { text: ' Age Band', value: 'AGE_BAND' },
          {
            text: 'Resource Util Band',
            value: 'RESOURCE_UTILIZATION_BAND',
          },

          {
            text: 'Hosp Major Morbidity Count',
            value: 'HOSPITAL_DOMINANT_MORBIDITY_TYPES',
          },
          {
            text: 'Chronic Count',
            value: 'CHRONIC_CONDITION_COUNT',
          },
          { text: 'Diagnostic Count', value: 'DIAGNOSES_USED' },
          { text: 'Details', value: 'DETAILS' },
        ];
      }
    },
    async cancelSearch() {
      if (this.cancelSource) {
        this.cancelSource.cancel('Start new search, stop active search');
      }
    },
    async openReferenceVsLocalRisk(ID, name, provName, observationPeriod) {
      this.AiSubReportModalOverlay = true;
      this.AiSubReportModalID = ID;
      this.observationPeriod = observationPeriod;
      this.memberName = name;
      this.provName = provName;
    },
    toggle() {
      if (this.selectedHeaders.length <= 8) {
        this.$nextTick(() => {
          const newHeaders = [
            { text: 'Provider Name', value: 'pcp_name', width: 200 },
            { text: 'Patient ID', value: 'ID' },
            { text: 'Patient Name', value: 'member_name' },
            { text: 'DOB', value: 'DOB' },
            { text: 'Age', value: 'AGE' },
            { text: 'Sex', value: 'SEX' },
            { text: 'Reference Risk', value: 'UNSCALED_RISK_SCORE' },
            { text: 'Local Risk', value: 'RESCALED_CONCURRENT_RISK' },
            ...this.headerOptions,
          ];
          this.selectedHeaders = newHeaders;
        });
      } else if (this.selectedHeaders.length > 8) {
        this.selectedHeaders = [
          { text: 'Provider Name', value: 'pcp_name', width: 200 },
          { text: 'Patient ID', value: 'ID' },
          { text: 'Patient Name', value: 'member_name' },
          { text: 'DOB', value: 'DOB' },
          { text: 'Age', value: 'AGE' },
          { text: 'Sex', value: 'SEX' },
          { text: 'Reference Risk', value: 'UNSCALED_RISK_SCORE' },
          { text: 'Local Risk', value: 'RESCALED_CONCURRENT_RISK' },
        ];
      }
    },
    getChipColor(num) {
      if (num > 0) {
        return 'red lighten-1';
      } if (num == 0) {
        return 'green lighten-1';
      }
    },
  },
  watch: {
    data(){
      this.reportData = this.data;
      this.filteredData = this.data;
      this.getHeaders()
      
    },
  },
};
</script>

<style scoped>
.export-button {
  position: absolute;
  left: 10px;
  bottom: 20px;
}
</style>
